import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { getAllPlanos } from './SearchPlanosServices';

interface SearchPlanosProps {
  isOpen: boolean;
  onClose: () => void;
  infoPlanoSelecionado: (infoPlano: any) => void;
}

interface PlanoOption {
  id: string;
  descricao: string;
  valor: number;
  duracao: number;
  frequenciaDias: number;
  tipoCobranca: string;
  listaAtividades?: any[];
}

export default function SearchPlanos({ isOpen, onClose, infoPlanoSelecionado }: SearchPlanosProps) {
  const [options, setOptions] = useState<PlanoOption[]>([]);
  const [selected, setSelected] = useState<PlanoOption | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelected(null);
    setLoading(true);
    getAllPlanos()
      .then((data) => {
        setOptions(data || []);
      })
      .catch(() => setOptions([]))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const handleConfirm = () => {
    if (!selected) return;

    const objInfoPlano = {
      idPlano: selected.id,
      duracao: selected.duracao,
      nomePlano: selected.descricao,
      atividadeId: selected.listaAtividades?.[0]?.id || '',
      valor: selected.valor,
      frequenciaDias: selected.frequenciaDias,
      tipoCobranca: selected.tipoCobranca || 'Mensal',
    };

    infoPlanoSelecionado(objInfoPlano);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Consulta Planos</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Selecione o plano desejado
        </DialogContentText>

        <Box mt={2}>
          <Autocomplete
            options={options}
            loading={loading}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return `${option.descricao} - R$ ${option.valor}`;
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selected}
            onChange={(_, newValue) => setSelected(newValue as PlanoOption | null)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {option.descricao}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.duracao === 9999
                        ? 'Duração: Ilimitado'
                        : `Duração: ${option.duracao} mês(es)`}
                      {' | '}{option.tipoCobranca || 'Mensal'}
                    </Typography>
                  </Box>
                  <Chip
                    label={`R$ ${option.valor}`}
                    size="small"
                    style={{ backgroundColor: '#7b0b99', color: 'white', fontWeight: 600 }}
                  />
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar plano"
                placeholder="Digite o nome do plano..."
              />
            )}
          />
        </Box>

        {selected && (
          <Box mt={2} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Plano:</strong> {selected.descricao}
            </Typography>
            <Typography variant="body2">
              <strong>Tipo de Cobrança:</strong> {selected.tipoCobranca || 'Mensal'}
            </Typography>
            <Typography variant="body2">
              <strong>Valor:</strong> R$ {selected.valor}
            </Typography>
            <Typography variant="body2">
              <strong>Duração:</strong> {selected.duracao === 9999 ? 'Ilimitado' : `${selected.duracao} mês(es)`}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancelar
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!selected}
          style={{ backgroundColor: '#7b0b99' }}>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
